import React from 'react';
import { Link } from 'react-router-dom';
import LogoutButton from './LogoutButton';
import { useAuth } from '../../Auth';
import { useUserGroups } from '../../services/authentication';
import type { Group } from '../../types';
import { useState, useRef, useEffect } from 'react';
import Logo from '../shared/Logo';

const NavigationBar: React.FC<{ onOpenNav?: () => void }> = ({ onOpenNav }) => {
  const { user, selectedGroup, setSelectedGroup } = useAuth();
  const {
    data: groups,
    isLoading: groupsLoading,
    error: groupsError
  } = useUserGroups(!!user);

  const [groupDropdownOpen, setGroupDropdownOpen] = useState(false);
  const [profileDropdownOpen, setProfileDropdownOpen] = useState(false);
  const groupDropdownRef = useRef<HTMLDivElement>(null);
  const profileDropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        groupDropdownRef.current &&
        !groupDropdownRef.current.contains(event.target as Node)
      ) {
        setGroupDropdownOpen(false);
      }
      if (
        profileDropdownRef.current &&
        !profileDropdownRef.current.contains(event.target as Node)
      ) {
        setProfileDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  return (
    <nav className="bg-surface-container-low border-b border-outline-variant/20 px-3 md:px-4 lg:px-1 py-1 sticky top-0 z-40">
      <div className="flex items-center justify-between">
        <div className="p-2 flex items-center gap-2 min-w-0">
          <button
            onClick={onOpenNav}
            aria-label="Open navigation menu"
            className="btn btn-ghost btn-circle md:hidden flex-shrink-0"
          >
            <span className="material-icons-outlined text-xl text-on-surface-variant">
              menu
            </span>
          </button>
          <Link to="/home" className="flex-shrink-0">
            <Logo className="h-8 md:h-10 w-auto" variant="invert" />
          </Link>
        </div>
        <div className="flex items-center gap-2 md:gap-3 min-w-0">
          <button className="btn btn-ghost btn-circle flex-shrink-0">
            <span className="material-icons-outlined text-lg text-on-surface-variant/40">
              help_outline
            </span>
          </button>

          {user && setSelectedGroup && (
            <div className="relative" ref={groupDropdownRef}>
              <button
                onClick={() => setGroupDropdownOpen(!groupDropdownOpen)}
                className="btn btn-ghost px-2 sm:px-4 normal-case text-on-surface font-semibold hover:bg-surface-container border border-outline-variant/20 flex items-center gap-2 min-w-0"
              >
                <div className="w-1.5 h-1.5 bg-tertiary flex-shrink-0"></div>
                <span className="truncate max-w-[14ch] sm:max-w-none">
                  {selectedGroup ? selectedGroup.title : 'Select Group'}
                </span>
                <span className="material-icons-outlined text-on-surface-variant/30 text-sm flex-shrink-0">
                  expand_more
                </span>
              </button>
              {groupDropdownOpen && (
                <div className="dropdown-menu mt-3">
                  <div className="px-4 py-2 mb-1">
                    <span className="text-[0.6rem] font-mono text-on-surface-variant/40 uppercase tracking-widest">
                      Your Workspaces
                    </span>
                  </div>
                  {groupsLoading && (
                    <div className="dropdown-item opacity-50">
                      <span>Loading...</span>
                    </div>
                  )}
                  {groupsError && (
                    <div className="dropdown-item text-error">
                      <span>Connection Error</span>
                    </div>
                  )}
                  {groups &&
                    (groups as Group[]).map((group: Group) => (
                      <button
                        key={group.id}
                        onClick={() => {
                          setSelectedGroup(group);
                          setGroupDropdownOpen(false);
                        }}
                        className="dropdown-item w-full"
                      >
                        <div className="w-7 h-7 bg-surface flex items-center justify-center font-bold text-on-surface text-[0.55rem] border border-outline-variant font-mono">
                          {group.title?.substring(0, 2).toUpperCase()}
                        </div>
                        {group.title}
                      </button>
                    ))}
                </div>
              )}
            </div>
          )}

          <div className="relative flex-shrink-0" ref={profileDropdownRef}>
            <button
              onClick={() => setProfileDropdownOpen(!profileDropdownOpen)}
              className="btn btn-ghost btn-circle avatar p-1"
            >
              <div className="w-8 h-8 overflow-hidden bg-tertiary border border-tertiary/30">
                <img
                  alt="User profile"
                  src={`https://ui-avatars.com/api/?name=${user?.email || 'User'}&background=D95600&color=F3F1EC&bold=true&size=128`}
                />
              </div>
            </button>
            {profileDropdownOpen && (
              <div className="dropdown-menu mt-3">
                <div className="px-4 py-3 mb-2 border-b border-outline-variant/20">
                  <p className="text-sm font-bold text-on-surface truncate">
                    {user?.email}
                  </p>
                  <p className="text-xs text-on-surface-variant/40 mt-0.5 font-mono">
                    Professional Account
                  </p>
                </div>
                {/* Profile link — commented out until profile page is built
                <Link
                  to="/home/profile"
                  className="dropdown-item"
                  onClick={() => setProfileDropdownOpen(false)}
                >
                  <span className="material-icons-outlined text-base">
                    person
                  </span>
                  Profile
                </Link>
                */}
                <Link
                  to="/home/settings"
                  className="dropdown-item"
                  onClick={() => setProfileDropdownOpen(false)}
                >
                  <span className="material-icons-outlined text-base">
                    settings
                  </span>
                  Settings
                </Link>
                <div className="divider mx-4 my-1 text-[6px]"></div>
                <div className="dropdown-item text-error hover:bg-error/10">
                  <span className="material-icons-outlined text-base">
                    logout
                  </span>
                  <LogoutButton />
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </nav>
  );
};

export default NavigationBar;
